import { inject, Service } from '@angular/core';
import { Observable, of } from 'rxjs';

// TYPE-ONLY — see the note in `subscription.http.repository.ts`: the port module
// imports this file as a value for its DI factory, so importing it back at
// runtime would close a cycle.
import type { CheckoutRequest, SubscriptionRepository } from '@domains/subscription/domain/interfaces/subscription.interface';
import {
  CardUpdateCheckout,
  ChargeVerification,
  CheckoutOutcome,
  DAY_MS,
  INITIAL_SUBSCRIPTION,
  SUBSCRIPTION_PRICE_SAR,
  SubscriptionCard,
  SubscriptionCatalog,
  SubscriptionInvoice,
  SubscriptionPlan,
  SubscriptionQuote,
  SubscriptionSnapshot,
  SubscriptionTier,
} from '@domains/subscription/domain/models/subscription.model';
import { StorageService } from '@core/storage/storage.service';
import { StorageKeys } from '@core/storage/storage.keys';

/** The offline catalog — the same two plans the live `/plans` endpoint serves. */
const MOCK_PLANS: readonly SubscriptionPlan[] = [
  {
    id: 1,
    name: 'Talbinah Premium Monthly',
    cycle: 'monthly',
    price: SUBSCRIPTION_PRICE_SAR,
    policy: { messageLimit: -1, voiceLimitWeeklyMin: -1, memoryEnabled: true, proactiveCapDaily: 10 },
  },
  {
    id: 2,
    name: 'Talbinah Premium Annual',
    cycle: 'annual',
    price: 86,
    policy: { messageLimit: -1, voiceLimitWeeklyMin: -1, memoryEnabled: true, proactiveCapDaily: 10 },
  },
] as const;

/**
 * SubscriptionMockRepository — the OFFLINE design harness (selected by
 * `SUBSCRIPTION_CONFIG.useMock`). A faithful port of the design store
 * (subscription-store.jsx): a localStorage tier machine with self-healing trial
 * auto-expiry. All storage goes through the SSR-safe StorageService (no-ops on
 * the server, so it reads as `free`).
 *
 * It answers the whole port so the UI can be exercised without a backend, but it
 * simulates only what it can honestly simulate: the tier machine and the plan
 * catalog. Billing history and saved cards come back EMPTY rather than invented,
 * so those screens exercise their real empty states; a mock checkout activates
 * immediately (there is no gateway to redirect to).
 */
@Service()
export class SubscriptionMockRepository implements SubscriptionRepository {
  private readonly storage = inject(StorageService);

  getSnapshot(): Observable<SubscriptionSnapshot> {
    return of(this.read());
  }

  plans(): Observable<SubscriptionCatalog> {
    return of({ plans: [...MOCK_PLANS], freePolicy: null });
  }

  startTrial(): Observable<Partial<SubscriptionSnapshot>> {
    const trialExpiresAt = this.now() + 7 * DAY_MS;
    this.storage.set(StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES, trialExpiresAt);
    this.write('trial');
    return of({ tier: 'trial' as SubscriptionTier, state: 'trial_active' as const, trialExpiresAt, hadTrial: true });
  }

  cancel(_reason?: string | null): Observable<Partial<SubscriptionSnapshot>> {
    // Mirror the server: cancelling keeps premium through a 30-day grace window.
    const graceEndsAt = this.now() + 30 * DAY_MS;
    this.storage.remove(StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES);
    this.storage.set(StorageKeys.SUBSCRIPTION_NEXT_BILL, graceEndsAt);
    this.write('premium');
    return of({
      tier: 'premium' as SubscriptionTier,
      state: 'cancelled_grace' as const,
      graceEndsAt,
      nextBillAt: graceEndsAt,
      trialExpiresAt: 0,
      hadTrial: true,
      autoRenew: false,
    });
  }

  subscribe(_request: CheckoutRequest): Observable<CheckoutOutcome> {
    return of(this.activate());
  }

  reactivate(_request: CheckoutRequest): Observable<CheckoutOutcome> {
    return of(this.activate());
  }

  quote(request: CheckoutRequest): Observable<SubscriptionQuote> {
    const price = MOCK_PLANS.find((p) => p.id === request.planId)?.price ?? SUBSCRIPTION_PRICE_SAR;
    const code = request.promoCode?.trim() || null;
    return of({
      planId: request.planId,
      cycle: request.cycle,
      currency: 'SAR',
      subtotal: price,
      discount: 0,
      walletApplied: 0,
      total: price,
      promoCode: code,
      // Offline we cannot validate a code — say so rather than approving it.
      promoValid: code ? false : null,
      promoMessage: null,
      walletBalance: null,
    });
  }

  setAutoRenew(on: boolean): Observable<Partial<SubscriptionSnapshot>> {
    return of({ autoRenew: on });
  }

  /** Offline there is no gateway to verify a card at — nothing to open, nothing stored. */
  updatePaymentMethod(_redirectUrl: string | null): Observable<CardUpdateCheckout> {
    return of({ url: null, message: null, amountDue: null, currency: 'SAR', willRefund: false });
  }

  verifyCharge(_chargeId: string): Observable<ChargeVerification> {
    return of({ mandateCaptured: null, reason: null });
  }

  invoices(): Observable<SubscriptionInvoice[]> {
    return of([]);
  }

  cards(): Observable<SubscriptionCard[]> {
    return of([]);
  }

  toolsCount(): Observable<number | null> {
    return of(null);
  }

  // ── internals ──
  /** Roll the local machine to an active paid month and report it as settled. */
  private activate(): CheckoutOutcome {
    const base = Math.max(this.now(), this.nextBillAt());
    this.storage.remove(StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES);
    this.storage.set(StorageKeys.SUBSCRIPTION_NEXT_BILL, base + 30 * DAY_MS);
    this.write('premium');
    return { kind: 'activated' };
  }

  /** Read tier + timers; a lapsed trial self-heals back to `free`. */
  private read(): SubscriptionSnapshot {
    const stored = this.storage.get<SubscriptionTier>(StorageKeys.SUBSCRIPTION_TIER) ?? 'free';
    const trialExpiresAt = this.trialExpiresAt();
    const nextBillAt = this.nextBillAt();
    let tier: SubscriptionTier = stored === 'trial' || stored === 'premium' ? stored : 'free';
    if (tier === 'trial' && trialExpiresAt && this.now() > trialExpiresAt) {
      this.storage.set(StorageKeys.SUBSCRIPTION_TIER, 'free');
      tier = 'free';
    }
    return {
      ...INITIAL_SUBSCRIPTION,
      tier,
      trialExpiresAt: tier === 'trial' ? trialExpiresAt : 0,
      nextBillAt: tier === 'premium' ? nextBillAt : 0,
      state: tier === 'trial' ? 'trial_active' : tier === 'premium' ? 'premium_active' : 'free_normal',
      hadTrial: !!trialExpiresAt,
      plan: tier === 'premium' ? { ...MOCK_PLANS[0]! } : null,
      cycle: tier === 'premium' ? 'monthly' : null,
    };
  }

  private write(tier: SubscriptionTier): void {
    this.storage.set(StorageKeys.SUBSCRIPTION_TIER, tier);
  }

  private trialExpiresAt(): number {
    return this.storage.get<number>(StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES) ?? 0;
  }

  private nextBillAt(): number {
    return this.storage.get<number>(StorageKeys.SUBSCRIPTION_NEXT_BILL) ?? 0;
  }

  private now(): number {
    return Date.now(); // SSR-safe: storage is a no-op on the server anyway
  }
}
